import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';

/**
 * Modal component for displaying planner regeneration progress
 * Features live log feed showing PDF generation progress with % complete
 */
const RegenerationModal = ({ isOpen, onClose, logs = [], progress = 0, totalPages = 0 }) => {
  const [isClosing, setIsClosing] = useState(false);
  const logContainerRef = useRef(null);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (logContainerRef?.current) {
      logContainerRef.current.scrollTop = logContainerRef?.current?.scrollHeight;
    }
  }, [logs]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose?.();
    }, 200);
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 transition-opacity duration-200 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={(e) => {
        if (e?.target === e?.currentTarget) {
          handleClose();
        }
      }}
    >
      <div 
        className={`bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 overflow-hidden transition-transform duration-200 ${
          isClosing ? 'scale-95' : 'scale-100'
        }`}
      >
        {/* Header */}
        <div className="bg-primary px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            <div>
              <h3 className="font-heading font-semibold text-white text-lg">
                Regenerating Planner
              </h3>
              <p className="font-body text-sm text-white/80">
                {progress > 0 ? `${progress}% complete` : 'Starting...'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={progress > 0 && progress < 100}
            className="text-white/80 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-border">
          <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
            <div 
              className="bg-primary h-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          {totalPages > 0 && (
            <p className="font-caption text-xs text-muted-foreground mt-2 text-center">
              Generating {totalPages} pages
            </p>
          )}
        </div>

        {/* Live Log Feed */}
        <div className="px-6 py-4">
          <div 
            ref={logContainerRef}
            className="bg-muted rounded-lg p-4 h-64 overflow-y-auto font-mono text-xs space-y-1"
          >
            {logs?.length === 0 ? (
              <p className="text-muted-foreground">Initializing...</p>
            ) : (
              logs?.map((log, index) => (
                <div key={index} className="text-foreground">
                  <span className="text-muted-foreground">[{log?.timestamp}]</span>{' '}
                  <span>{log?.message}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-muted/50 border-t border-border">
          <p className="font-body text-sm text-muted-foreground text-center">
            {progress === 100 ? 
              'Generation complete! Preparing for download...' : 
              'Please wait while your planner is being regenerated. This may take a few moments.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegenerationModal;